import styled, { css, keyframes } from 'styled-components';

import {
  EVEN_ROW_HOVER,
  GADGET_HEADERS_BACKGROUND_COLOR,
  HEADER_SPAN_CSS,
  HEADERS_BACKGROUND_COLOR,
  HEADERS_BORDER_BOTTOM_COLOR,
  HEADERS_BORDER_COLOR,
  MATRIX_BOLD_CELL_CLASS,
  MATRIX_COMPARISON_VALUE_HEADER_CLASS,
  ODD_ROW_COLOR,
  ODD_ROW_HOVER,
  PADDING,
  SEARCH_HEADER_SPAN_CSS,
  SEARCH_SPAN_CSS,
  SPAN_CSS,
} from 'components/Grid/StyleOverrides';
import MEDIA_QUERIES from 'mediaQueries';

// The table matches the AG theme (StyleOverrides) using the same tokens, so
// light and dark mode both come for free. Every rule is ours, so nothing
// needs !important.

// fill: the table scrolls inside its container. natural: as tall as its rows
// (AG's domLayout autoHeight), scrolling sideways only. print: as tall as its
// rows and as wide as it needs, for the scheduled-report PDF
export type ScrollerLayout = 'fill' | 'natural' | 'print';

const SCROLLER_LAYOUT_CSS: { [layout in ScrollerLayout]: string } = {
  fill: 'height: 100%; overflow: auto;',
  natural: 'height: auto; overflow-x: auto; overflow-y: hidden;',
  print: 'height: auto; overflow: visible;',
};

export const Scroller = styled.div<{
  layout: ScrollerLayout;
  hasBorders: boolean;
}>`
  position: relative;
  width: 100%;
  ${(props) => SCROLLER_LAYOUT_CSS[props.layout]}
  overscroll-behavior-x: contain;
  /* AG's hasBorders (GRID_BORDERS_CSS) */
  ${(props) =>
    props.hasBorders &&
    css`
      box-sizing: border-box;
      border-width: 1px;
      border-style: solid;
      border-color: ${HEADERS_BORDER_COLOR};
    `}
`;

// Below the smallest breakpoint, AG's header falls back to the Balham
// theme's height (--ag-header-height: 8 x its 4px grid size)
const HEADER_HEIGHT_CSS = css`
  height: 32px;

  @media ${MEDIA_QUERIES.DESKTOP_XXS} {
    height: 22px;
  }
  @media ${MEDIA_QUERIES.DESKTOP_XS} {
    height: 26px;
  }
  @media ${MEDIA_QUERIES.DESKTOP_S} {
    height: 26px;
  }
  @media ${MEDIA_QUERIES.DESKTOP_M} {
    height: 28px;
  }
  @media ${MEDIA_QUERIES.DESKTOP_L} {
    height: 31px;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Table = styled.table<{
  minWidth?: number;
  isClickable: boolean;
  isMeasuring: boolean;
  isHeaderSkippedWhenMeasuring: boolean;
  hasDarkHeader: boolean;
  isSearchScreen: boolean;
  isReloading: boolean;
}>`
  border-collapse: separate;

  /* Reloading rows that are still showing: a light shade over the whole
     table (header, totals and pinned columns too, however it's scrolled),
     under the spinner. It fades in, so a quick refetch doesn't flash */
  ${(props) =>
    props.isReloading &&
    css`
      position: relative;

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 3;
        background-color: var(--color-grid-loading-overlay);
        pointer-events: none;
        animation: ${fadeIn} 150ms ease-in;
      }
    `}
  border-spacing: 0;
  table-layout: auto;
  /* fitContent fills the width too: its cells don't wrap, so they keep
     their content width and the filler column takes what's left */
  width: 100%;
  min-width: ${(props) => (props.minWidth ? `${props.minWidth}px` : '100%')};
  background-color: var(--color-bg-primary);
  color: var(--color-grid-cell-text);
  /* The cell and header text sizes (SPAN_CSS, HEADER_SPAN_CSS) only start at
     the smallest desktop breakpoint. Below it AG's text falls back to the
     Balham theme's --ag-font-size, so DataTable's does too, instead of the
     page's much larger default. */
  font-size: 12px;

  th,
  td {
    padding: 0 ${PADDING}px;
    vertical-align: middle;
    text-align: left;
    white-space: nowrap;
    box-sizing: border-box;
  }

  th:first-child,
  td:first-child {
    padding-left: ${PADDING * 2}px;
  }

  th:last-child,
  td:last-child {
    padding-right: ${PADDING * 2}px;
  }

  /* Pinned cells sit over the others while scrolling, so they need the row's
     own (opaque) background */
  th:focus-visible,
  td:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }

  .dt-pinned {
    position: sticky;
    z-index: 1;
    background-color: inherit;
  }

  .dt-filler {
    width: 100%;
    padding: 0;
  }

  th.dt-filler {
    border-right: none;
  }

  td.dt-pinned-right {
    border-left: 1px solid var(--color-grid-border-row);
  }

  td.dt-pinned-left {
    border-right: 1px solid var(--color-grid-border-row);
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: ${HEADERS_BACKGROUND_COLOR};
  }

  thead tr {
    background-color: ${HEADERS_BACKGROUND_COLOR};
  }

  /* AG's hasDarkHeader (GRID_DARK_HEADER_CSS) */
  ${(props) =>
    props.hasDarkHeader &&
    css`
      thead,
      thead tr {
        background-color: ${GADGET_HEADERS_BACKGROUND_COLOR};
      }
    `}

  th {
    ${HEADER_HEIGHT_CSS};
    color: var(--color-grid-header-text);
    border-bottom: 1px solid ${HEADERS_BORDER_BOTTOM_COLOR};
    white-space: normal;

    span {
      ${HEADER_SPAN_CSS};
    }

    &:not(:last-child) {
      border-right: 1px solid ${HEADERS_BORDER_COLOR};
    }

    &.dt-sortable {
      cursor: pointer;
    }

    &.dt-group {
      text-align: center;
    }

    &.rightAlignedHeader {
      text-align: right;
    }

    &.${MATRIX_COMPARISON_VALUE_HEADER_CLASS} {
      border-right: 1px solid ${HEADERS_BORDER_COLOR};
    }
  }

  tbody tr {
    cursor: ${(props) => (props.isClickable ? 'pointer' : 'auto')};
    background-color: var(--color-bg-primary);

    &.dt-odd {
      background-color: ${ODD_ROW_COLOR};
    }

    &:hover {
      background-color: ${EVEN_ROW_HOVER};
    }

    &.dt-odd:hover {
      background-color: ${ODD_ROW_HOVER};
    }

    &.dt-selected,
    &.dt-selected:hover {
      background-color: var(--color-bg-selected);
    }

    .ag-row-show-on-hover {
      display: none;
    }

    &:hover .ag-row-show-on-hover {
      display: block;
    }
  }

  tbody td {
    border-bottom: 1px solid var(--color-grid-border-row);

    span {
      ${SPAN_CSS};
    }
  }

  /* The empty space between the rows and the totals (see useBodyFillHeight):
     no row lines, stripes or hover, as AG's empty viewport */
  tbody tr.dt-fill,
  tbody tr.dt-fill:hover {
    background-color: var(--color-bg-primary);
    cursor: auto;
  }

  tbody tr.dt-fill td {
    border-bottom: none;
    padding: 0;
  }

  tfoot {
    position: sticky;
    bottom: 0;
    z-index: 2;
  }

  tfoot tr {
    background-color: var(--color-bg-primary);
  }

  tfoot td {
    border-top: 1px solid var(--color-grid-border-row);
    color: var(--color-brand-dark);
    font-weight: bold;

    span {
      ${HEADER_SPAN_CSS};
    }
  }

  /* The measuring pass (see DataTable's lockedWidths): every column at its
     content's natural width */
  ${(props) =>
    props.isMeasuring &&
    css`
      width: max-content;
      min-width: 0;

      .dt-filler {
        display: none;
      }
    `}

  ${(props) =>
    props.isHeaderSkippedWhenMeasuring &&
    css`
      thead th > * {
        display: none;
      }
    `}

    .${MATRIX_BOLD_CELL_CLASS}, .${MATRIX_BOLD_CELL_CLASS} * {
    font-weight: bold;
  }

  /* The Search screen's larger text (AG's ag-theme-fleetops-search) */
  ${(props) =>
    props.isSearchScreen &&
    css`
      th span,
      tfoot td span {
        ${SEARCH_HEADER_SPAN_CSS};
      }

      tbody td span {
        ${SEARCH_SPAN_CSS};
      }
    `}

  /* Drag a movable column's header to move it: the columns move as it's
     dragged (see ColumnDragGhost for the label that follows the pointer).
     The dragged header is highlighted, as AG's moving header is, and struck
     through when letting go would hide it */
  th.dt-movable {
    user-select: none;
  }

  th.dt-dragging {
    background-color: var(--color-bg-selected);
  }

  th.dt-drag-hide {
    text-decoration: line-through;
  }

  /* Drag a resizable column's header edge to change its width. A pinned
     header is already positioned (sticky), so the handle sits in it as is */
  th.dt-resizable:not(.dt-pinned) {
    position: relative;
  }

  .dt-resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    touch-action: none;
    z-index: 1;

    &:hover {
      background-color: var(--color-grid-border-alt);
    }
  }

  /* Header components written for AG (DeltaHeaderCell, MetricHeaderCell)
     draw their sort arrow with AG's icon font, which only exists inside AG's
     theme */
  .ag-icon {
    font-family: inherit;
    font-style: normal;
  }

  .ag-icon-asc::before {
    content: '↑';
  }

  .ag-icon-desc::before {
    content: '↓';
  }
`;

// AG cells are a full-height flex row; the renderers rely on that
export const CellContent = styled.div<{ height: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${(props) => props.height}px;
  width: 100%;
  overflow: hidden;
`;

export const HeaderContent = styled.div<{
  isRightAligned: boolean;
  isCentered: boolean;
}>`
  display: flex;
  /* A right-aligned header's sort arrow sits before its label, as AG draws
     it */
  flex-direction: ${(props) =>
    props.isRightAligned && !props.isCentered ? 'row-reverse' : 'row'};
  align-items: center;
  justify-content: ${(props) => (props.isCentered ? 'center' : 'flex-start')};
  height: 100%;
  gap: 2px;
`;

export const Expander = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: 4px;
  cursor: pointer;
  color: var(--color-grid-cell-text);
  display: flex;
  align-items: center;
`;

// Over slide-outs and modals, as AG raises its popups (.ag-popup)
export const POPUP_Z_INDEX = 90001;

// A cell editor's popup, styled as AG's .ag-popup-editor (global-styles)
export const EditorPopup = styled.div`
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-xs);
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
`;

// The label that follows the pointer while a header is dragged, as AG's
// drag ghost. Placed by transform from the window's top left
export const ColumnDragGhostDiv = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${POPUP_Z_INDEX};
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  padding: 2px 8px;
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-xs);
  border: 1px solid var(--color-border-secondary);
  border-radius: 6px;
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  pointer-events: none;
`;
